import { handTypesForProsthesis } from '../hands/constants';
import { ProsthesisType } from '../prosthesis/constants';

/**
 * Konfiguracja łokcia Espire — to, co protetyk wybiera na nowym ekranie (GLA-63).
 *
 * Wartości WYŁĄCZNIE z arkusza „SKU 2025" i komentarza Alicji z 06.10 (GLA-66). Druga strona
 * tej listy żyje w `api/app/Support/EspireConfiguration.php`: tam nazwa koloru jest tłumaczona
 * na trzycyfrowy kod SKU, a żądanie jest walidowane względem tych samych napisów. Rozjazd między
 * stronami daje zapis odrzucony przez walidację albo SKU bez kodu koloru — pilnuje tego
 * `tests/unit/constants/espireConfiguration.spec.ts`.
 *
 * Nazwy rozmiarów w TYM brzmieniu — Alicja prosiła o nie wprost, a generator SKU mapuje je
 * na kody 8 i 9.
 */
export const ESPIRE_SIZES = ['Small (45 mm wrist)', 'Standard (50 mm wrist)'] as const;

export const ESPIRE_COLORS = [
	'Brown',
	'Caucasian',
	'White',
	'Silver',
	'Jet Black',
	'Carbon Fibre',
] as const;

/**
 * Strona pokrętła przeciwwagi. Wartość jedzie do bazy małymi literami, etykieta jest dla
 * człowieka — stąd para, a nie goła lista napisów.
 */
export const ESPIRE_DIAL_SIDES = [
	{ value: 'medial', label: 'Medial' },
	{ value: 'lateral', label: 'Lateral' },
] as const;

/** Wymaganie z dokumentu 09.26: pokrętło domyślnie po stronie przyśrodkowej. */
export const ESPIRE_DEFAULT_DIAL_SIDE = 'medial';

/**
 * Komunikat pod polami — widoczny ZAWSZE, przy każdym modelu.
 *
 * Brzmienie z dokumentu, bez przeredagowywania.
 */
export const ESPIRE_CONTROL_CONFIGURATION_NOTE =
	'For the Pro and Hybrid models, the control configuration (electrodes, inputs etc.) is ' +
	'determined on a case-by-case basis - we will contact you before confirming your order.';

const ESPIRE_SLUGS: string[] = [
	ProsthesisType.EspireProAbove,
	ProsthesisType.EspireHybridAbove,
	ProsthesisType.EspireClassicPlusAbove,
	ProsthesisType.EspireClassicAbove,
];

/**
 * Czy PRODUKT zamówienia jest łokciem Espire.
 *
 * Potrzebne tam, gdzie podsumowanie zamówienia wylicza pozycje: `mergedItem` buduje się
 * z `order.prosthesis`, a dla Espire to JEST ten łokieć — więc dołożenie go drugi raz
 * z `order.elbow` dawało dwa kafelki tego samego elementu (znalazł Cerber, GLA-63 r6;
 * ta sama klasa co duplikat w kolejce produkcyjnej z r3).
 */
export function isEspireProsthesis(prosthesisSlug?: string | null): boolean {
	return !!prosthesisSlug && ESPIRE_SLUGS.includes(prosthesisSlug);
}

/**
 * Czy produkt Espire ma w ścieżce krok elektrod i baterii (GLA-89).
 *
 * Reguła wisi WYŁĄCZNIE na matrycy produktu (`HAND_TYPES_BY_PROSTHESIS`, GLA-85) i celowo
 * NIE pyta o stan zamówienia. To nie jest uproszczenie — to jedyny wariant, który działa.
 *
 * `savesDrafts.saveBeforeNext` wylicza krok do ZAPISANIA ze stanu SPRZED zapisu, a rękę i terminal
 * zapisuje dopiero wołany przez niego `updateSocket`/`updateTerminal`. Każda reguła czytająca
 * `bionic_hands` albo `terminals` jest więc w tym jednym momencie nieaktualna — i myli się w OBIE
 * strony, co pokazały dwie rundy recenzji:
 *   - wariant „pytaj o zapisaną rękę": świeże zamówienie zapisywało krok NASTĘPNY po elektrodach,
 *     więc wznowiony szkic ekranu elektrod już nie pokazywał (Rita, r1),
 *   - wariant „pytaj dodatkowo o terminal": zmiana ręki z pasywnej na mioelektryczną w Classic Plus
 *     czytała STARY terminal i gubiła krok tak samo (Cerber, r2).
 *
 * Bez stanu zamówienia rozjazd przed/po zapisie jest niemożliwy, a ścieżka zgadza się ze ścieżką
 * SUROWĄ, po której i tak liczy postęp `compareStepToOrder`.
 *
 * Cenę płaci jeden przypadek: Classic Plus z ręką pasywną ma krok w ścieżce i wejdzie na ekran
 * elektrod, z którego `ViewExtraParameters` przepuszcza go dalej (`exitStepFromExtraParameters`).
 * To przekierowanie, nie błąd — i jest tańsze niż zamówienie, które po cichu traci elektrody.
 */
export function espireShowsExtraParameters(prosthesisSlug?: string | null): boolean {
	return handTypesForProsthesis(prosthesisSlug).some(type => type.slug === 'bionic-hand');
}
